import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserService } from './user.service';

export type Theme = 'light' | 'dark';

const THEME_STORAGE_KEY = 'app-theme';
const DEFAULT_THEME: Theme = 'light';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private currentTheme$ = new BehaviorSubject<Theme>(DEFAULT_THEME);
  private document: Document;

  constructor(@Inject(DOCUMENT) private doc: Document, private userService: UserService) {
    this.document = doc;
  }

  /**
   * Inicializa o tema, dando preferência às preferências do usuário
   * e caindo para o localStorage como fallback.
   */
  public initTheme(): void {
    const userPref = this.userService?.currentUser?.preferences?.themeMode;
    if (userPref) {
      this.applyUserThemePreference(userPref);
      return;
    }

    const savedTheme = this.getStoredTheme();
    const theme = savedTheme || DEFAULT_THEME;
    this.setTheme(theme, false);
  }

  /**
   * Define o tema atual
   * @param theme - Nome do tema ('light' ou 'dark')
   * @param saveToStorage - Se deve salvar no localStorage (padrão: true)
   */
  public setTheme(theme: Theme, saveToStorage: boolean = true): void {
    if (saveToStorage) {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    }

    // Remove classes de tema anteriores
    this.document.body.classList.remove('theme-light', 'theme-dark', 'theme-evollo', 'theme-kainos');

    // Adiciona a nova classe de tema
    this.document.body.classList.add(`theme-${theme}`);

    // Atualiza o BehaviorSubject
    this.currentTheme$.next(theme);
  }

  /**
   * Retorna o tema atual
   */
  public getCurrentTheme(): Theme {
    return this.currentTheme$.value;
  }

  /**
   * Observable do tema atual
   */
  public getTheme$(): Observable<Theme> {
    return this.currentTheme$.asObservable();
  }

  /**
   * Alterna entre os temas disponíveis
   */
  public toggleTheme(): void {
    const currentTheme = this.getCurrentTheme();
    const newTheme: Theme = currentTheme === 'light' ? 'dark' : 'light';
    this.setTheme(newTheme);

    // Atualiza a preferência do usuário em memória
    try {
      const prefTheme = this.toPrefTheme(newTheme);
      if (this.userService?.currentUser) {
        if (!this.userService.currentUser.preferences) {
          this.userService.currentUser.preferences = {};
        }
        this.userService.currentUser.preferences.themeMode = prefTheme;

        // Persiste no backend
        this.userService.updateCurrentUserPreferences({ themeMode: prefTheme }).subscribe({
          next: () => {},
          error: (err) => {
            // Mantemos o tema aplicado e logamos o erro
            console.error('Falha ao salvar preferências de tema', err);
          }
        });
      }

      // Espelha no localStorage como fallback temporário
      localStorage.setItem(THEME_STORAGE_KEY, newTheme);
    } catch (e) {
      console.error('Erro ao atualizar preferência de tema local', e);
    }
  }

  /**
   * Aplica preferência de tema do usuário (LIGHT/DARK) sem salvar em storage
   */
  public applyUserThemePreference(themeMode?: 'LIGHT' | 'DARK'): void {
    const theme = this.toUiTheme(themeMode);
    this.setTheme(theme, false);
  }

  /**
   * Retorna o tema armazenado no localStorage
   * Suporta migração de temas antigos (evollo/kainos) para novos (light/dark)
   */
  private getStoredTheme(): Theme | null {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    
    // Migração de temas antigos
    if (stored === 'evollo') {
      localStorage.setItem(THEME_STORAGE_KEY, 'light');
      return 'light';
    }
    if (stored === 'kainos') {
      localStorage.setItem(THEME_STORAGE_KEY, 'dark');
      return 'dark';
    }
    
    if (stored === 'light' || stored === 'dark') {
      return stored as Theme;
    }
    return null;
  }

  private toUiTheme(pref?: 'LIGHT' | 'DARK'): Theme {
    return pref === 'DARK' ? 'dark' : 'light';
  }

  private toPrefTheme(ui: Theme): 'LIGHT' | 'DARK' {
    return ui === 'dark' ? 'DARK' : 'LIGHT';
  }
}

